'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var zod = require('zod');
var countries = require('i18n-iso-countries');
var retry = require('async-retry');
var diary = require('diary');
var radash = require('radash');
var murmurhashJs = require('murmurhash-js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var countries__default = /*#__PURE__*/_interopDefaultLegacy(countries);
var retry__default = /*#__PURE__*/_interopDefaultLegacy(retry);

const Query = zod.z.record(zod.z.string(), zod.z.string());

const Page = zod.z.object({
  path: zod.z.string(),
  query: Query,
  referrer: zod.z.string(),
  search: zod.z.string(),
  url: zod.z.string()
});

const SessionStatistics = zod.z.object({
  id: zod.z.string(),
  isReturningVisitor: zod.z.boolean(),
  landingPage: Page,
  count: zod.z.number(),
  activeSessionLength: zod.z.number(),
  averageSessionLength: zod.z.number()
});

const Alpha2Code = zod.z.enum(['unknown', ...Object.keys(countries__default["default"].getAlpha2Codes())]);
const GeoLocation = zod.z.object({
  coordinates: zod.z.object({
    latitude: zod.z.number(),
    longitude: zod.z.number()
  }).optional(),
  city: zod.z.string().optional(),
  postalCode: zod.z.string().optional(),
  region: zod.z.string().optional(),
  regionCode: zod.z.string().optional(),
  country: zod.z.string().optional(),
  countryCode: Alpha2Code.optional(),
  continent: zod.z.string().optional(),
  timezone: zod.z.string().optional()
});

const JsonLiteral = zod.z.lazy(() => zod.z.union([zod.z.string(), zod.z.number(), zod.z.boolean(), zod.z.null(), zod.z.symbol(), zod.z.array(JsonLiteral), JsonLiteralObject]));
const JsonLiteralObject = zod.z.record(zod.z.string(), zod.z.union([JsonLiteral, zod.z.array(JsonLiteral)]));
const Json = zod.z.lazy(() => zod.z.union([JsonLiteral, zod.z.array(Json), zod.z.record(Json)]));
const JsonObject = zod.z.record(Json);

const Profile = zod.z.object({
  id: zod.z.string(),
  stableId: zod.z.string(),
  random: zod.z.number(),
  audiences: zod.z.array(zod.z.string()),
  traits: JsonObject,
  location: GeoLocation,
  session: SessionStatistics
});

const RequestBodyOptions = zod.z.object({
  features: zod.z.array(zod.z.string()).optional()
});

const SelectedVariantInfo = zod.z.object({
  experienceId: zod.z.string(),
  variantIndex: zod.z.number(),
  variants: zod.z.record(zod.z.string()),
  sticky: zod.z.boolean().optional().default(false)
});

const withResponseEnvelope = dataSchema => zod.z.object({
  data: dataSchema,
  message: zod.z.string(),
  error: zod.z.boolean().nullable()
});

exports.ComponentTypeEnum = void 0;
(function (ComponentTypeEnum) {
  ComponentTypeEnum["EntryReplacement"] = "EntryReplacement";
  ComponentTypeEnum["InlineVariable"] = "InlineVariable";
})(exports.ComponentTypeEnum || (exports.ComponentTypeEnum = {}));
exports.InlineVariableComponentValueTypeEnum = void 0;
(function (InlineVariableComponentValueTypeEnum) {
  InlineVariableComponentValueTypeEnum["String"] = "String";
  InlineVariableComponentValueTypeEnum["Object"] = "Object";
  InlineVariableComponentValueTypeEnum["Boolean"] = "Boolean";
  InlineVariableComponentValueTypeEnum["Number"] = "Number";
})(exports.InlineVariableComponentValueTypeEnum || (exports.InlineVariableComponentValueTypeEnum = {}));
const allowVariableTypeSchema = zod.z.union([zod.z.string(), zod.z.boolean(), zod.z.number(),
// TODO: this causes the type to resolve to unknown
JsonObject]);
const variableVariantSchema = zod.z.object({
  value: allowVariableTypeSchema
});

exports.ChangeTypes = void 0;
(function (ChangeTypes) {
  ChangeTypes["Variable"] = "Variable";
})(exports.ChangeTypes || (exports.ChangeTypes = {}));
const ChangeBase = zod.z.object({
  key: zod.z.string(),
  type: zod.z.nativeEnum(exports.ChangeTypes),
  meta: zod.z.object({
    experienceId: zod.z.string(),
    variantIndex: zod.z.number()
  })
});
const VariableChange = ChangeBase.extend({
  type: zod.z.literal(exports.ChangeTypes.Variable),
  value: allowVariableTypeSchema
});
const Change = zod.z.discriminatedUnion('type', [VariableChange]);

const CreateProfileRequestBody = zod.z.object({
  events: zod.z.array(zod.z.unknown()).min(1),
  options: RequestBodyOptions.optional()
});
const CreateProfileResponse = withResponseEnvelope(zod.z.object({
  profile: Profile,
  experiences: zod.z.array(SelectedVariantInfo),
  changes: zod.z.array(Change)
}));

const UpdateProfileRequestBody = zod.z.object({
  events: zod.z.array(zod.z.unknown()).min(1),
  options: RequestBodyOptions.optional()
});
const UpdateProfileResponse = withResponseEnvelope(zod.z.object({
  profile: Profile,
  experiences: zod.z.array(SelectedVariantInfo),
  changes: zod.z.array(Change)
}));

const UpsertManyProfilesRequestBody = zod.z.object({
  events: zod.z.array(zod.z.unknown()).min(1),
  options: RequestBodyOptions.optional()
});
const UpsertManyProfilesResponse = withResponseEnvelope(zod.z.object({
  profiles: zod.z.array(Profile).optional()
}));

const Campaign = zod.z.object({
  name: zod.z.string().optional(),
  source: zod.z.string().optional(),
  medium: zod.z.string().optional(),
  term: zod.z.string().optional(),
  content: zod.z.string().optional()
});

const PageviewProperties = zod.z.object({
  path: zod.z.string(),
  query: Query,
  referrer: zod.z.string(),
  search: zod.z.string(),
  title: zod.z.string(),
  url: zod.z.string(),
  category: zod.z.string().optional()
}).catchall(Json);

const Pageview = 'page';
const Track = 'track';
const Identify = 'identify';
const Screen = 'screen';
const Group = 'group';
const Alias = 'alias';
const Component = 'component';

const SerializableValue = zod.z.lazy(() => zod.z.union([zod.z.string(), zod.z.number(), zod.z.boolean(), zod.z.null(),
// eslint-disable-next-line @typescript-eslint/no-use-before-define
SerializableObject, zod.z.array(SerializableValue)]));
const SerializableObject = zod.z.lazy(() => zod.z.record(zod.z.string(), zod.z.union([SerializableValue, zod.z.array(SerializableValue)])));

const LEGACY_ANONYMOUS_ID = '__anon_id';
const ANONYMOUS_ID = '__nt_anonymous_id__';
const DEBUG_FLAG = '__nt_debug__';
const PROFILE_FALLBACK_CACHE = '__nt_profile__';
const EXPERIENCES_FALLBACK_CACHE = '__nt_experiences__';
const PROFILE_CHANGE = 'profile-change';
const PROFILE_RESET = 'profile-reset';
const CONSENT = '__nt-consent__';
const EMPTY_MERGE_ID = 'nt:empty-merge-id';
const NINETAILED_ANONYMOUS_ID_COOKIE = 'ntaid';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

class Logger {
  constructor() {
    this.name = '@ninetailed/experience.js';
    this.sinks = [];
    this.diary = diary.diary(this.name, this.onLogEvent.bind(this));
    diary.enable(this.name);
  }
  addSink(sink) {
    this.sinks = [...this.sinks.filter(existingSink => existingSink.name !== sink.name), sink];
  }
  removeSink(name) {
    this.sinks = this.sinks.filter(sink => sink.name !== name);
  }
  removeSinks() {
    this.sinks = [];
  }
  debug(message, ...args) {
    this.diary.debug(message, ...args);
  }
  info(message, ...args) {
    this.diary.info(message, ...args);
  }
  log(message, ...args) {
    this.diary.log(message, ...args);
  }
  warn(message, ...args) {
    this.diary.warn(message, ...args);
  }
  error(message, ...args) {
    this.diary.error(message, ...args);
  }
  fatal(message, ...args) {
    this.diary.fatal(message, ...args);
  }
  onLogEvent(event) {
    this.sinks.forEach(sink => {
      sink.ingest(event);
    });
  }
}
const logger = new Logger();

const fetchTimeout = (url, init, {
  fetchImpl: _fetchImpl = fetch
} = {}) => __awaiter(void 0, void 0, void 0, function* () {
  const {
    timeout,
    onTimeout
  } = init;
  const controller = new AbortController();
  const id = setTimeout(() => {
    if (typeof onTimeout === 'function') {
      logger.error(new Error('Ninetailed Request timed out.'));
    }
    controller.abort();
  }, timeout);
  const response = yield _fetchImpl(url, Object.assign(Object.assign({}, init), {
    signal: controller.signal
  }));
  clearTimeout(id);
  return response;
});

const GetProfileResponse = withResponseEnvelope(zod.z.object({
  profile: Profile,
  experiences: zod.z.array(SelectedVariantInfo),
  changes: zod.z.array(Change)
}));

const BASE_URL = 'https://experience.ninetailed.co';
const FEATURES = {
  IP_ENRICHMENT: 'ip-enrichment',
  LOCATION: 'location'
};
const DEFAULT_ENVIRONMENT = 'main';
class HttpError extends Error {
  constructor(message, status = 500) {
    super(message);
    this.status = status;
    Object.setPrototypeOf(this, HttpError.prototype);
  }
}
class NinetailedApiClient {
  constructor({
    clientId,
    environment = DEFAULT_ENVIRONMENT,
    url = BASE_URL,
    fetchImpl = fetch
  }) {
    this.constructOptions = options => {
      const bodyOptions = {};
      if (options.enabledFeatures && Array.isArray(options.enabledFeatures) && options.enabledFeatures.length > 0) {
        bodyOptions.features = options.enabledFeatures;
      }
      return bodyOptions;
    };
    this.clientId = clientId;
    this.environment = environment;
    this.url = url;
    this.fetchImpl = fetchImpl;
  }
  logRequestError(error, {
    requestName
  }) {
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        logger.warn(`${requestName} request aborted due to network issues. This request is not retryable.`);
      } else {
        logger.error(`${requestName} request failed with error: [${error.name}] ${error.message}`);
      }
    }
  }
  upsertProfile({
    profileId,
    events
  }, options) {
    return __awaiter(this, void 0, void 0, function* () {
      if (!profileId) {
        return this.createProfile({
          events
        }, options);
      } else {
        return this.updateProfile({
          profileId: profileId,
          events
        }, options);
      }
    });
  }
  retryRequest(request, name, options) {
    var _a, _b;
    return retry__default["default"](bail => __awaiter(this, void 0, void 0, function* () {
      try {
        const response = yield request;
        if (response.status === 503) {
          throw new HttpError(`${name} request failed with status: "[${response.status}] ${response.statusText}".`, 503);
        }
        if (!response.ok) {
          bail(new Error(`${name} request failed with status: "[${response.status}] ${response.statusText} - traceparent: ${response.headers.get('traceparent')}". This request is not retryable`));
          return null;
        }
        logger.debug(`${name} response: `, response);
        return response;
      } catch (error) {
        if (error instanceof HttpError && error.status === 503) {
          throw error;
        }
        if (error instanceof Error) {
          bail(error);
        } else {
          bail(new Error(`${name} request failed with an unknown error. This request is not retryable.`));
        }
        return null;
      }
    }), {
      retries: (_a = options.retries) !== null && _a !== void 0 ? _a : 1,
      minTimeout: (_b = options.minRetryTimeout) !== null && _b !== void 0 ? _b : 0,
      onRetry: (error, attempt) => logger.error(`${error.message} Retrying (attempt ${attempt}).`)
    });
  }
  makeProfileMutationRequest(url, payload, name, options) {
    return this.retryRequest(fetchTimeout(this.constructUrl(url, options), {
      method: 'POST',
      headers: this.constructHeaders(options),
      body: JSON.stringify(payload),
      timeout: options.timeout || 3000
    }, {
      fetchImpl: this.fetchImpl
    }), name, options);
  }
  getProfile(id, options = {}) {
    return __awaiter(this, void 0, void 0, function* () {
      const requestName = 'Get Profile';
      logger.info(`Sending ${requestName} request.`);
      try {
        const response = yield this.retryRequest(fetchTimeout(this.constructUrl(`/v2/organizations/${this.clientId}/environments/${this.environment}/profiles/${id}`, options), {
          method: 'GET',
          timeout: options.timeout || 3000
        }, {
          fetchImpl: this.fetchImpl
        }), 'Get Profile', options);
        const {
          data
        } = GetProfileResponse.parse(yield response.json());
        logger.debug(`${requestName} request succesfully completed.`);
        return data;
      } catch (error) {
        this.logRequestError(error, {
          requestName
        });
        throw error;
      }
    });
  }
  /**
   * Creates a profile and returns it.
   * Use the given profileId for subsequent update requests.
   * The events will be used to aggregate the new Profile state.
   */
  createProfile({
    events
  }, options = {}) {
    return __awaiter(this, void 0, void 0, function* () {
      const requestName = 'Create Profile';
      logger.info(`Sending ${requestName} request.`);
      const body = {
        events,
        options: this.constructOptions(options)
      };
      logger.debug(`${requestName} request Body: `, body);
      try {
        const response = yield this.makeProfileMutationRequest(`/v2/organizations/${this.clientId}/environments/${this.environment}/profiles`, body, 'Create Profile', options);
        const {
          data
        } = CreateProfileResponse.parse(yield response.json());
        logger.debug(`${requestName} request succesfully completed.`);
        return data;
      } catch (error) {
        this.logRequestError(error, {
          requestName
        });
        throw error;
      }
    });
  }
  /**
   * Updates a profile with the given profileId.
   * The events will be used to aggregate the new Profile state.
   */
  updateProfile({
    profileId,
    events
  }, options = {}) {
    return __awaiter(this, void 0, void 0, function* () {
      const requestName = 'Update Profile';
      logger.info(`Sending ${requestName} request.`);
      const body = {
        events,
        options: this.constructOptions(options)
      };
      logger.debug(`${requestName} request Body: `, body);
      try {
        const response = yield this.makeProfileMutationRequest(`/v2/organizations/${this.clientId}/environments/${this.environment}/profiles/${profileId}`, body, requestName, options);
        const {
          data
        } = UpdateProfileResponse.parse(yield response.json());
        logger.debug(`${requestName} request successfully completed.`);
        return data;
      } catch (error) {
        this.logRequestError(error, {
          requestName
        });
        throw error;
      }
    });
  }
  /**
   * Sends multiple events to the Ninetailed API.
   * Every events needs to have a anonymous ID.
   * Profiles will get created or updated according to the set anonymous ID.
   *
   * This method is intended to be used from server environments.
   */
  upsertManyProfiles({
    events
  }, options = {}) {
    return __awaiter(this, void 0, void 0, function* () {
      const requestName = 'Upsert Many Profiles';
      logger.info(`Sending ${requestName} request.`);
      const body = {
        events,
        options: this.constructOptions(options)
      };
      logger.debug(`${requestName} request Body: `, body);
      try {
        const response = yield this.makeProfileMutationRequest(`/v2/organizations/${this.clientId}/environments/${this.environment}/events`, body, requestName, Object.assign({
          plainText: false
        }, options));
        const {
          data: {
            profiles
          }
        } = UpsertManyProfilesResponse.parse(yield response.json());
        logger.debug(`${requestName} request successfully completed.`);
        return profiles;
      } catch (error) {
        this.logRequestError(error, {
          requestName
        });
        throw error;
      }
    });
  }
  constructUrl(path, options) {
    const url = new URL(path, this.url);
    if (options.preflight) {
      url.searchParams.set('type', 'preflight');
    }
    if (options.locale) {
      url.searchParams.set('locale', options.locale);
    }
    return url.toString();
  }
  constructHeaders(options) {
    var _a;
    const headers = new Map();
    if (options.ip) {
      headers.set('X-Force-IP', options.ip);
    }
    if ((_a = options.plainText) !== null && _a !== void 0 ? _a : true) {
      headers.set('Content-Type', 'text/plain');
    } else {
      headers.set('Content-Type', 'application/json');
    }
    return Object.fromEntries(headers);
  }
}

class ConsoleLogSink {
  constructor() {
    this.name = 'ConsoleLogSink';
  }
  ingest(event) {
    const consoleLogMap = {
      debug: console.debug,
      info: console.info,
      log: console.log,
      warn: console.warn,
      error: console.error,
      fatal: console.error
    };
    const logFn = consoleLogMap[event.level];
    if (logFn) {
      logFn(...event.messages);
    }
  }
}

class OnLogLogSink {
  constructor(onLog) {
    this.onLog = onLog;
    this.name = 'OnLogLogSink';
  }
  ingest(event) {
    if (!event.messages.length) {
      return;
    }
    if (event.level == 'error' || event.level === 'fatal') {
      return;
    }
    if (typeof event.messages[0] !== 'string') {
      return;
    }
    this.onLog(event.messages[0], ...event.messages.slice(1));
  }
}

class OnErrorLogSink {
  constructor(onError) {
    this.onError = onError;
    this.name = 'OnErrorLogSink';
  }
  ingest(event) {
    if (!event.messages.length) {
      return;
    }
    if (event.level === 'error' || event.level === 'fatal') {
      if (event.messages[0] instanceof Error || typeof event.messages[0] === 'string') {
        this.onError(event.messages[0], ...event.messages.slice(1));
      }
    }
  }
}

const buildQuery = url => {
  const result = {};
  try {
    const parsedUrl = new URL(url);
    parsedUrl.searchParams.forEach((value, key) => {
      result[key] = value;
    });
  } catch (error) {
    //noop
  }
  return result;
};

const buildCampaign = ctx => {
  const params = buildQuery(ctx.url);
  const campaign = {};
  if (params['utm_campaign']) {
    campaign.name = params['utm_campaign'];
  }
  if (params['utm_source']) {
    campaign.source = params['utm_source'];
  }
  if (params['utm_medium']) {
    campaign.medium = params['utm_medium'];
  }
  if (params['utm_term']) {
    campaign.term = params['utm_term'];
  }
  if (params['utm_content']) {
    campaign.content = params['utm_content'];
  }
  return campaign;
};

const buildPage = ctx => {
  try {
    const query = buildQuery(ctx.url);
    const url = new URL(ctx.url);
    return {
      path: url.pathname,
      query,
      referrer: ctx.referrer,
      search: url.search,
      url: ctx.url
    };
  } catch (error) {
    return {
      path: '',
      query: {},
      referrer: '',
      search: '',
      url: ''
    };
  }
};

const buildEvent = ({
  messageId,
  timestamp,
  type,
  ctx,
  location
}) => {
  const date = new Date(timestamp).toISOString();
  return {
    channel: 'web',
    context: {
      library: {
        name: 'Ninetailed React Analytics SDK',
        version: "7.18.7" 
      },
      userAgent: ctx.userAgent,
      campaign: buildCampaign(ctx),
      locale: ctx.locale,
      page: buildPage(ctx),
      gdpr: {
        isConsentGiven: true
      },
      location
    },
    messageId: messageId,
    originalTimestamp: date,
    timestamp: date,
    sentAt: date,
    type
  };
};

const buildPageEvent = data => {
  var _a;
  return Object.assign(Object.assign({}, buildEvent(Object.assign(Object.assign({}, data), {
    type: 'page'
  }))), {
    properties: Object.assign(Object.assign(Object.assign({}, data.properties), buildPage(data.ctx)), {
      title: ((_a = data.ctx.document) === null || _a === void 0 ? void 0 : _a.title) || ''
    })
  });
};

const buildTrackEvent = data => {
  return Object.assign(Object.assign({}, buildEvent(Object.assign(Object.assign({}, data), {
    type: 'track'
  }))), {
    event: data.event,
    properties: data.properties
  });
};

const buildIdentifyEvent = data => {
  return Object.assign(Object.assign({}, buildEvent(Object.assign(Object.assign({}, data), {
    type: 'identify'
  }))), {
    traits: data.traits,
    userId: data.userId
  });
};

const buildComponentViewEvent = data => {
  return Object.assign(Object.assign({}, buildEvent(Object.assign(Object.assign({}, data), {
    type: 'component'
  }))), {
    componentType: data.componentType,
    componentId: data.componentId,
    experienceId: data.experienceId,
    variantIndex: data.variantIndex
  });
};

const isBrowser = () => typeof window !== 'undefined';

const pipe = (...fns) => value => fns.reduce((acc, fn) => {
  return fn(acc);
}, value);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const unionBy = (array1, array2, prop) => {
  return Object.values([...array1, ...array2].reduce((acc, item) => {
    if (!(prop in acc)) {
      acc[item[prop]] = item;
    }
    return acc;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, {}));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const pickBy = (object, predicate) => {
  return Object.keys(object).reduce((acc, key) => {
    if (predicate(object[key], key)) {
      acc[key] = object[key];
    }
    return acc;
  }, {});
};

/**
 *
 * This function is a copy of the radash `template` function
 * https://github.com/rayepps/radash/blob/c378bd1bc401045ed7d8e5e47b275d2159ce43d5/src/string.ts#L118
 */
const template = (str, data, regex = /\{\{(.+?)\}\}/g) => {
  return Array.from(str.matchAll(regex)).reduce((acc, match) => {
    var _a;
    return acc.replace(match[0], (_a = radash.get(data, match[1].trim())) !== null && _a !== void 0 ? _a : 'undefined');
  }, str);
};

const isPageViewEvent = event => {
  return event.type === 'page';
};
const isTrackEvent = event => {
  return event.type === 'track';
};
const isIdentifyEvent = event => {
  return event.type === 'identify';
};
const isComponentViewEvent = event => {
  return event.type === 'component';
};

/*
    cycle.js
    2021-05-31

    Public Domain.

    NO WARRANTY EXPRESSED OR IMPLIED. USE AT YOUR OWN RISK.

    https://github.com/douglascrockford/JSON-js/blob/master/cycle.js

    USE YOUR OWN COPY. IT IS EXTREMELY UNWISE TO LOAD CODE FROM SERVERS YOU DO
    NOT CONTROL.
*/
const decycle = function decycle(object, replacer) {
  // Make a deep copy of an object or array, assuring that there is at most
  // one instance of each object or array in the resulting structure. The
  // duplicate references (which might be forming cycles) are replaced with
  // an object of the form
  //      {"$ref": PATH}
  // where the PATH is a JSONPath string that locates the first occurance.
  // So,
  //      const a = [];
  //      a[0] = a;
  //      return JSON.stringify(decycle(a));
  // produces the string '[{"$ref":"$"}]'.
  // If a replacer function is provided, then it will be called for each value.
  // A replacer function receives a value and returns a replacement value.
  // JSONPath is used to locate the unique object. $ indicates the top level of
  // the object or array. [NUMBER] or [STRING] indicates a child element or
  // property.
  const objects = new WeakMap(); // object to path mappings
  return function derez(value, path) {
    // The derez function recurses through the object, producing the deep copy.
    // If a replacer function was provided, then call it to get a replacement value.
    if (replacer !== undefined) {
      value = replacer(value);
    }
    // typeof null === "object", so go on if this value is really an object but not
    // one of the weird builtin objects.
    if (typeof value === 'object' && value !== null && !(value instanceof Boolean) && !(value instanceof Date) && !(value instanceof Number) && !(value instanceof RegExp) && !(value instanceof String)) {
      // If the value is an object or array, look to see if we have already
      // encountered it. If so, return a {"$ref":PATH} object.
      const oldPath = objects.get(value);
      if (oldPath !== undefined) {
        return {
          $ref: oldPath
        };
      }
      // Otherwise, accumulate the unique value and its path.
      objects.set(value, path);
      // If it is an array, replicate the array.
      if (Array.isArray(value)) {
        const newItem = [];
        value.forEach(function (element, i) {
          newItem[i] = derez(element, path + '[' + i + ']');
        });
        return newItem;
      } else {
        // If it is an object, replicate the object.
        const newItem = {};
        Object.keys(value).forEach(function (name) {
          newItem[name] = derez(value[name], path + '[' + JSON.stringify(name) + ']');
        });
        return newItem;
      }
    }
    return value;
  }(object, '$');
};

const circularJsonStringify = value => {
  return JSON.stringify(decycle(value));
};

const EXPERIENCE_TRAIT_PREFIX = 'nt_experiment_';

const LOWER_BOUND = 0;
const UPPER_BOUND = 4294967295;
const normalize = (val, min, max) => (val - min) / (max - min);
const getRandom = text => {
  const hash = murmurhashJs.murmur3(text, 0);
  const random = normalize(hash, LOWER_BOUND, UPPER_BOUND);
  return random;
};
const getTrafficRandom = (profile,
// eslint-disable-next-line @typescript-eslint/no-explicit-any
experience) => getRandom(`traffic-${experience.id}-${profile.stableId}`);
const getDistributionRandom = (profile,
// eslint-disable-next-line @typescript-eslint/no-explicit-any
experience) => getRandom(`distribution-${experience.id}-${profile.stableId}`);

const isExperienceMatch = ({
  experience,
  profile
}) => {
  const trafficRandom = getTrafficRandom(profile, experience);
  logger.info(`The traffic random factor for experience ${experience.id} is ${trafficRandom}. It's traffic allocation is set to ${experience.trafficAllocation}.`);
  const isInTrafficRange = experience.trafficAllocation > trafficRandom;
  const matchesAudience = !experience.audience || profile.audiences.includes(experience.audience.id);
  logger.info(`Is the profile in traffic allocation range? ${isInTrafficRange ? 'yes' : 'no'}.\n
      Does the profile match the audience of the experience? ${matchesAudience ? 'yes' : 'no'}.\n`);
  return isInTrafficRange && matchesAudience;
};

const pickExperimentTraits = profile => {
  const experimentTraits = {};
  for (const key in profile.traits) {
    if (key.startsWith(EXPERIENCE_TRAIT_PREFIX) && typeof profile.traits[key] === 'boolean' && profile.traits[key] === true) {
      experimentTraits[key] = profile.traits[key];
    }
  }
  return experimentTraits;
};
const selectActiveExperiments = (experiments, profile) => {
  const experimentTraits = pickExperimentTraits(profile);
  const experimentTraitsIds = Object.keys(experimentTraits).map(id => id.replace(EXPERIENCE_TRAIT_PREFIX, ''));
  const activeExperiments = experiments.filter(experiment => experimentTraitsIds.includes(experiment.id));
  return activeExperiments;
};

const selectBaselineWithVariants = (experience, baseline) => {
  const component = experience.components.find(entryReplacement => 'id' in entryReplacement.baseline && entryReplacement.baseline.id === baseline.id);
  return (component === null || component === void 0 ? void 0 : component.type) === exports.ComponentTypeEnum.EntryReplacement ? component : null;
};

const selectDistribution = ({
  experience,
  profile
}) => {
  const distributionRandom = getDistributionRandom(profile, experience);
  logger.log(`The distribution random factor for experience ${experience.id} is ${distributionRandom}. It's distribution is set to ${JSON.stringify(experience.distribution, null, 2)}.`);
  const distribution = experience.distribution.find(({
    start,
    end
  }) => distributionRandom >= start && distributionRandom <= end // this overlaps on one value for each boundary but we just find the first match
  );

  if (!distribution) {
    return {
      index: 0,
      start: 0,
      end: 1
    };
  }
  return distribution;
};

const selectExperience = ({
  experiences,
  profile
}) => {
  const selectedExperience = experiences.find(experience => isExperienceMatch({
    experience,
    profile
  }));
  return selectedExperience !== null && selectedExperience !== void 0 ? selectedExperience : null;
};

const selectVariants = (experience, baseline) => {
  const baselineWithVariants = selectBaselineWithVariants(experience, baseline);
  if (!baselineWithVariants) {
    return [];
  }
  return baselineWithVariants.variants;
};

const selectHasVariants = (experience, baseline) => {
  const variants = selectVariants(experience, baseline);
  return variants.length > 0;
};

const selectVariant = ({
  baseline,
  experience,
  profile
}) => {
  const variants = selectVariants(experience, baseline);
  const baselineVariant = Object.assign(Object.assign({}, baseline), {
    hidden: false
  });
  if (!variants.length) {
    return {
      variant: baselineVariant,
      index: 0
    };
  }
  const distribution = selectDistribution({
    experience,
    profile
  });
  if (!distribution) {
    return {
      variant: baselineVariant,
      index: 0
    };
  }
  if (distribution.index === 0) {
    return {
      variant: baselineVariant,
      index: 0
    };
  }
  const selectableVariants = [baselineVariant, ...variants];
  if (selectableVariants.length <= distribution.index) {
    logger.warn("A distribution for a variant was selected but it's metadata could not be found.");
    return {
      variant: baselineVariant,
      index: 0
    };
  }
  const variant = selectableVariants[distribution.index];
  return {
    variant,
    index: distribution.index
  };
};

exports.ANONYMOUS_ID = ANONYMOUS_ID;
exports.Alias = Alias;
exports.Alpha2Code = Alpha2Code;
exports.CONSENT = CONSENT;
exports.Campaign = Campaign;
exports.Change = Change;
exports.Component = Component;
exports.ConsoleLogSink = ConsoleLogSink;
exports.CreateProfileRequestBody = CreateProfileRequestBody;
exports.CreateProfileResponse = CreateProfileResponse;
exports.DEBUG_FLAG = DEBUG_FLAG;
exports.EMPTY_MERGE_ID = EMPTY_MERGE_ID;
exports.EXPERIENCES_FALLBACK_CACHE = EXPERIENCES_FALLBACK_CACHE;
exports.EXPERIENCE_TRAIT_PREFIX = EXPERIENCE_TRAIT_PREFIX;
exports.FEATURES = FEATURES;
exports.GeoLocation = GeoLocation;
exports.Group = Group;
exports.Identify = Identify;
exports.Json = Json;
exports.JsonLiteral = JsonLiteral;
exports.JsonLiteralObject = JsonLiteralObject;
exports.JsonObject = JsonObject;
exports.LEGACY_ANONYMOUS_ID = LEGACY_ANONYMOUS_ID;
exports.Logger = Logger;
exports.NINETAILED_ANONYMOUS_ID_COOKIE = NINETAILED_ANONYMOUS_ID_COOKIE;
exports.NinetailedApiClient = NinetailedApiClient;
exports.OnErrorLogSink = OnErrorLogSink;
exports.OnLogLogSink = OnLogLogSink;
exports.PROFILE_CHANGE = PROFILE_CHANGE;
exports.PROFILE_FALLBACK_CACHE = PROFILE_FALLBACK_CACHE;
exports.PROFILE_RESET = PROFILE_RESET;
exports.Page = Page;
exports.Pageview = Pageview;
exports.PageviewProperties = PageviewProperties;
exports.Profile = Profile;
exports.Properties = JsonObject;
exports.Query = Query;
exports.RequestBodyOptions = RequestBodyOptions;
exports.Screen = Screen;
exports.SelectedVariantInfo = SelectedVariantInfo;
exports.SerializableObject = SerializableObject;
exports.SerializableValue = SerializableValue;
exports.SessionStatistics = SessionStatistics;
exports.Track = Track;
exports.Traits = JsonObject;
exports.UpdateProfileRequestBody = UpdateProfileRequestBody;
exports.UpdateProfileResponse = UpdateProfileResponse;
exports.UpsertManyProfilesRequestBody = UpsertManyProfilesRequestBody;
exports.UpsertManyProfilesResponse = UpsertManyProfilesResponse;
exports.VariableChange = VariableChange;
exports.allowVariableTypeSchema = allowVariableTypeSchema;
exports.buildCampaign = buildCampaign;
exports.buildComponentViewEvent = buildComponentViewEvent;
exports.buildEvent = buildEvent;
exports.buildIdentifyEvent = buildIdentifyEvent;
exports.buildPage = buildPage;
exports.buildPageEvent = buildPageEvent;
exports.buildQuery = buildQuery;
exports.buildTrackEvent = buildTrackEvent;
exports.circularJsonStringify = circularJsonStringify;
exports.fetchTimeout = fetchTimeout;
exports.getDistributionRandom = getDistributionRandom;
exports.getTrafficRandom = getTrafficRandom;
exports.isBrowser = isBrowser;
exports.isComponentViewEvent = isComponentViewEvent;
exports.isExperienceMatch = isExperienceMatch;
exports.isIdentifyEvent = isIdentifyEvent;
exports.isPageViewEvent = isPageViewEvent;
exports.isTrackEvent = isTrackEvent;
exports.logger = logger;
exports.pickBy = pickBy;
exports.pickExperimentTraits = pickExperimentTraits;
exports.pipe = pipe;
exports.selectActiveExperiments = selectActiveExperiments;
exports.selectBaselineWithVariants = selectBaselineWithVariants;
exports.selectDistribution = selectDistribution;
exports.selectExperience = selectExperience;
exports.selectHasVariants = selectHasVariants;
exports.selectVariant = selectVariant;
exports.selectVariants = selectVariants;
exports.template = template;
exports.unionBy = unionBy;
exports.variableVariantSchema = variableVariantSchema;
